import { useCallback } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/auth';
import { useToast } from '@/components/toast';
import { ACTION_PARAMS } from '@/hooks/use-filters-panel-state';
import { useOptionalWishlistOverlay } from '@/providers/wishlist-overlay-context';

export interface RequireAuthOptions {
    actionName: string;
    getActionParams?: (...args: unknown[]) => Record<string, unknown>;
    getReturnUrl?: () => string;
    toastMessage?: string;
    /**
     * `toast` (default): show sign-in / sign-up in a toast.
     * `redirect`: go to `/login?returnUrl=...` immediately.
     * `loginDrawer`: open the shared LoginDrawer (requires WishlistOverlayProvider); falls back to toast if absent.
     */
    unauthenticatedBehavior?: 'toast' | 'redirect' | 'loginDrawer';
}

function getCleanReturnUrl(baseReturnUrl: string): string {
    const url = new URL(baseReturnUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    for (const key of ACTION_PARAMS) {
        url.searchParams.delete(key);
    }
    return url.pathname + url.search;
}

/**
 * Hook that wraps an action function to require authentication.
 * If the user is not authenticated, shows a toast with Sign In and Sign Up buttons,
 * preserves the action metadata, and redirects to auth pages with returnUrl.
 *
 * After authentication, the action will be automatically executed.
 *
 * @example
 * ```tsx
 * const handleAddToWishlist = useRequireAuth(
 *   async (productId: string) => {
 *     await addToWishlist(productId);
 *   },
 *   {
 *     actionName: 'addToWishlist',
 *     getActionParams: (productId) => ({ productId }),
 *     getReturnUrl: () => window.location.pathname,
 *     toastMessage: 'Sign in to add items to your wishlist'
 *   }
 * );
 * ```
 */
export function useRequireAuth<T extends (...args: unknown[]) => Promise<unknown>>(
    action: T,
    options: RequireAuthOptions
): T {
    const { t } = useTranslation(['product', 'login', 'signup']);
    const session = useAuth();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const wishlistOverlay = useOptionalWishlistOverlay();

    return useCallback(
        async (...args: Parameters<T>) => {
            // Re-check auth state in case it changed (e.g., after signup/login)
            const currentIsAuthenticated = Boolean(session?.userType === 'registered' && session?.customerId);

            // If authenticated, execute action immediately
            if (currentIsAuthenticated) {
                return action(...args);
            }

            // Preserve action metadata - encode in returnUrl (URL-based approach)
            const actionParams = options.getActionParams?.(...args) || {};
            const baseReturnUrl = options.getReturnUrl?.() || window.location.pathname + window.location.search;
            const cleanReturnUrl = getCleanReturnUrl(baseReturnUrl);
            const actionParamsString = Object.keys(actionParams).length > 0 ? JSON.stringify(actionParams) : undefined;

            // Build returnUrl with action params embedded (URL-based approach)
            const returnUrlWithAction = new URL(baseReturnUrl, window.location.origin);
            returnUrlWithAction.searchParams.set('action', options.actionName);
            if (actionParamsString) {
                returnUrlWithAction.searchParams.set('actionParams', actionParamsString);
            }
            const encodedReturnUrl = returnUrlWithAction.pathname + returnUrlWithAction.search;

            // Build auth URLs with encoded returnUrl
            const baseAuthUrl = (path: string) => {
                const url = new URL(path, window.location.origin);
                url.searchParams.set('returnUrl', encodedReturnUrl);
                return url.pathname + url.search;
            };

            const loginUrl = baseAuthUrl('/login');
            // Registration is handled by the unified email-first flow on /login
            const signupUrl = baseAuthUrl('/login');

            if (options.unauthenticatedBehavior === 'loginDrawer' && wishlistOverlay) {
                wishlistOverlay.openLoginDrawer({
                    returnUrl: cleanReturnUrl,
                    action: options.actionName,
                    actionParams: actionParamsString,
                });
            } else if (options.unauthenticatedBehavior === 'redirect') {
                void navigate(loginUrl);
            } else {
                // Show toast with Sign In and Sign Up buttons
                addToast(options.toastMessage || t('product:signInToContinue'), 'info', {
                    duration: 8000, // Longer duration for actionable toast
                    action: {
                        label: t('login:signIn'),
                        onClick: () => {
                            void navigate(loginUrl);
                        },
                    },
                    cancel: {
                        label: t('signup:form.createAccountButton'),
                        onClick: () => {
                            void navigate(signupUrl);
                        },
                    },
                });
            }

            // Return a rejected promise to indicate action was intercepted
            // Note: This error is internal and won't be displayed to users
            return Promise.reject(new Error('Authentication required'));
        },
        [session, action, options, navigate, addToast, t, wishlistOverlay]
    ) as T;
}
